import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { createStyles, Theme, WithStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import moment from 'moment';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import CustomDialog from '../../../../components/CustomDialog';
import { useLoadingDialog } from '../../../../hooks/backdrop';
import { useFeedback } from '../../../../hooks/feedback';
import { EmployeePunchCardDTO } from '../../../../models/dto/EmployeePunchCardDTO';
import { api } from '../../../../services/api';
import { AppState } from '../../../../store';
import { EmployeesReducerData } from '../../../../store/reducers/employeesReducer';

moment.locale('pt-br');

interface EmployeePunchCardDataRequest {
  justificativa: string;
  listaPontos: Pick<EmployeePunchCardDTO, 'data' | 'horarios'>[];
}

type AddRegistersDialogProps = {
  open: boolean;
  registerDateSelected: string;
  registersSelected: string[];
  handleCloseModal: () => void;
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      width: 400,
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
  });

export interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

const AddRegistersDialog: React.FC<AddRegistersDialogProps> = ({
  open,
  registerDateSelected,
  registersSelected,
  handleCloseModal,
}) => {
  const { addFeedback } = useFeedback();
  const { handleLoadingDialog } = useLoadingDialog();
  const [newRegisters, setNewRegisters] = useState<string[]>([]);
  const [registers, setRegisters] = useState<string[]>([]);
  const [justification, setJustification] = useState<string>('Esquecimento');

  const { employeeSelected }: EmployeesReducerData = useSelector(
    (state: AppState) => state.employeesReducer,
  );

  const buildEmployeePunchCardRequest = (): EmployeePunchCardDataRequest => {
    const register: Pick<EmployeePunchCardDTO, 'data' | 'horarios'> = {
      data: moment(registerDateSelected).format('DD/MM/YYYY'),
      horarios: newRegisters,
    };

    return { listaPontos: [register], justificativa: justification };
  };

  const submit = async () => {
    handleLoadingDialog(true);
    const employeeRegisters = buildEmployeePunchCardRequest();
    try {
      await api.post(`/gdp/pontos/${employeeSelected?.numeroCadastro}`, {
        ...employeeRegisters,
      });

      addFeedback({
        type: 'success',
        closeTimeout: 7000,
        description: 'Marcação adicionada com sucesso!',
        onClose: () =>
          addFeedback({
            type: 'info',
            description:
              'O processamento dessa informação não é instantâneo, portanto levará algum tempo até que a informação seja exibida nas marcações do funcionário.',
            onClose: () => handleCloseModal(),
          }),
      });
    } catch (error) {
      console.log(error);
      addFeedback({
        type: 'error',
        description: 'Ocorreu um erro ao realizar esta operação.',
        onClose: () => handleCloseModal(),
      });
    } finally {
      handleLoadingDialog(false);
    }
  };

  const handleEditNewRegister = (index: number, value: string) => {
    const newList = [
      ...newRegisters.slice(0, index),
      value,
      ...newRegisters.slice(index + 1),
    ];

    setNewRegisters(newList);
  };

  const handleAddNewRegister = () => {
    setNewRegisters([...newRegisters, moment().format('HH:mm')]);
  };

  const removeNewRegister = (registerIndex: number) => {
    const newList = newRegisters.filter(
      (item, index) => index !== registerIndex,
    );
    setNewRegisters(newList);
  };

  useEffect(() => {
    if (registersSelected.length > 0) {
      setRegisters(registersSelected.filter(horario => horario !== 'Folga'));
    }
  }, [registersSelected]);

  return (
    <CustomDialog
      maxWidth="sm"
      fullWidth
      handleClose={handleCloseModal}
      handleConfirm={submit}
      showCloseButton
      title="Adicionar Marcação"
      okButtonLabel="Salvar"
      disabledOkButton={justification.length === 0 || newRegisters.length === 0}
      contentDividers
      open={open}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={9}>
          <Typography color="textPrimary" variant="subtitle1">
            Colaborador:
          </Typography>
          <Typography color="textPrimary" variant="subtitle2">
            {employeeSelected && employeeSelected.nome}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Typography color="textPrimary" variant="subtitle1">
            Data:
          </Typography>
          <Typography color="textPrimary" variant="subtitle2">
            {moment(registerDateSelected).format('DD/MM/YYYY')}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography color="textPrimary" variant="subtitle1">
            Horários:
          </Typography>
        </Grid>
        {registers.map((horario, index) => {
          return (
            <Grid key={index} item xs={12} sm={3}>
              <Typography color="textPrimary" variant="subtitle2">
                {horario}
              </Typography>
            </Grid>
          );
        })}

        {newRegisters &&
          newRegisters.map((newRegister, index) => {
            return (
              <Grid
                key={index}
                container
                alignItems="center"
                item
                xs={12}
                sm={3}
              >
                <Grid item xs={12} sm={7}>
                  <Typography color="primary" variant="subtitle2">
                    <TextField
                      type="time"
                      fullWidth
                      value={newRegister}
                      onChange={(
                        event: ChangeEvent<
                          HTMLInputElement | HTMLTextAreaElement
                        >,
                      ) => handleEditNewRegister(index, event.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        step: 300, // 5 min
                      }}
                    />
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Tooltip title="Remover marcação">
                    <IconButton onClick={() => removeNewRegister(index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            );
          })}

        {registers.length + newRegisters.length < 6 && (
          <Tooltip title="Adicionar marcação">
            <IconButton onClick={handleAddNewRegister}>
              <AddIcon />
            </IconButton>
          </Tooltip>
        )}

        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Justificativa</InputLabel>
            <Select
              fullWidth
              value={justification}
              onChange={event => setJustification(String(event.target.value))}
            >
              <MenuItem value="Esquecimento">Esquecimento</MenuItem>
              <MenuItem value="Falha">Falha</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </CustomDialog>
  );
};

export default AddRegistersDialog;
